import {
    ANALYTICS_ELAPSED_TIME,
    ANALYTICS_ACTION_BUTTON_CLICKED,
    ANALYTICS_KEYBOARD_PRESSED,
    ANALYTICS_REGISTRATION_CLOSED,
    ANALYTICS_REGISTRATION_BTN_CLICKED,
    ANALYTICS_DESKTOP_SUCCESS_VIEWED,
    ANALYTICS_DESKTOP_SUCCESS_DONE_CLICKED,
    ANALYTICS_EXP_SELECTED,
} from 'aria-viewer/analytics/analyticActions'
import { trackEvent } from 'aria-viewer/core/middleware/analyticsMiddleware'
import * as EVENTS from 'aria-viewer/analytics/constants/desktopAnalytics'
import { getTourData } from 'aria-viewer/analytics/utils/analyticUtils'
import CTAPromptTypes from 'aria-viewer/tour/constants/CTAPromptTypes'
import ModalContentTypes from 'aria-viewer/modal/constants/ModalContentTypes'

const selectKeyboardAnalyticsAction = keyPressed => {
    // Determines what action to send based on key press
    switch (keyPressed) {
        case 88:
            return EVENTS.EXP_REGISTRATION_KEYBOARD_PRESSED
        default:
            return EVENTS.EXP_KEYBOARD_PRESSED
    }
}

/**
 * Checks if it's a registration modal,
 * if it is send the data to mixpanel
 * @param getState
 * @param content   [content of the modal]
 * @param eventName
 */
const checkRegistrationModal = (getState, content, eventName) => {
    if (content.name === ModalContentTypes.REGISTRATION) {
        const { inst_name, loc_name, owner_id, loc_id, stoptitle } = getTourData()(getState)

        const initiatedBy = EVENTS.INIT_BY[content.initBy]

        return trackEvent(eventName, {
            Institution: inst_name,
            Location: loc_name,
            Inst_ID: owner_id,
            Loc_ID: loc_id,
            Stop: stoptitle,
            Initiated_by: initiatedBy,
        })
    }
}

const registrationPageData = (getState, action) => {
    const state = getState()
    const { loc_name, loc_id, inst_name, owner_id, stoptitle } = getTourData()(getState)
    const currentModal = state.getIn(['modal', 'modalStack', 0])
    const { initBy } = currentModal.content
    const { data } = action

    return {
        Institution: inst_name,
        Location: loc_name,
        Inst_ID: owner_id,
        Loc_ID: loc_id,
        Stop: stoptitle,
        Initiated_by: EVENTS.INIT_BY[initBy],
        data,
    }
}

/**
 * returns initiatedby for cta buttons
 */
const getCTAInitiatedBy = state => {
    const initTypes = {
        [CTAPromptTypes.ACT_NOW]: 'Act Now',
        [CTAPromptTypes.WHATS_NEXT]: "What's Next",
        EXPERIENCE: 'Experience',
    }

    const ctaPrompt = state.get('ctaPrompt')

    if (ctaPrompt.get('isCTAPromptOpen')) {
        return initTypes[ctaPrompt.get('ctaPromptType')]
    } else {
        return initTypes.EXPERIENCE
    }
}

const analyticAnalytics = {
    // Action triggered to alert how much time has elapsed for the purposes
    // of analytics
    [ANALYTICS_ELAPSED_TIME]: (getState, action) => {
        const { loc_name, loc_id, inst_name, owner_id, userkey, type, totalStops, stoptitle } = getTourData()(getState)

        return trackEvent(EVENTS.EXP_10_SECOND_MARK, {
            Institution: inst_name,
            Location: loc_name,
            Inst_ID: owner_id,
            Loc_ID: loc_id,
            Userkey: userkey,
            Exp_Type: type,
            Total_Stops: totalStops,
            Stop: stoptitle,
        })
    },

    [ANALYTICS_ACTION_BUTTON_CLICKED]: (getState, action) => {
        const { name, color, actionName = 'register' } = action
        const { loc_name, inst_name, stoptitle, userkey, actions } = getTourData()(getState)

        return trackEvent(EVENTS.EXP_CTABUTTON_CLICKED, {
            Institution: inst_name,
            Location: loc_name,
            Stop: stoptitle,
            Name: name,
            Action: actionName,
            Color: color,
            Userkey: userkey,
            Total_actions: actions.length,
            Initiated_by: getCTAInitiatedBy(getState()),
        })
    },

    [ANALYTICS_KEYBOARD_PRESSED]: (getState, action) => {
        const { keyPressed, actionName } = action
        const analyticsAction = selectKeyboardAnalyticsAction(keyPressed)
        const { loc_name, inst_name, stoptitle, userkey, actions } = getTourData()(getState)

        return trackEvent(analyticsAction, {
            Institution: inst_name,
            Location: loc_name,
            Stop: stoptitle,
            Name: name,
            Action: actionName,
            Userkey: userkey,
            Total_actions: actions.length,
        })
    },

    [ANALYTICS_REGISTRATION_CLOSED]: (getState, action) => {
        const modal = getState().getIn(['modal', 'modalStack', 0])
        if (modal) {
            return checkRegistrationModal(getState, modal.content, EVENTS.EXP_REG_CLOSED)
        }
    },

    [ANALYTICS_REGISTRATION_BTN_CLICKED]: (getState, action) => {
        const { Location, Loc_ID, Institution, Inst_ID, Initiated_by, Stop, data } = registrationPageData(
            getState,
            action
        )

        let properties = {
            Location,
            Loc_ID,
            Institution,
            Inst_ID,
            Initiated_by,
            Stop,
        }

        return trackEvent(EVENTS.EXP_REG_FINISHBUTTON_CLICKED, properties)
    },

    [ANALYTICS_DESKTOP_SUCCESS_VIEWED]: (getState, action) => {
        const modal = getState().getIn(['modal', 'modalStack', 0])
        if (modal) {
            return checkRegistrationModal(getState, modal.content, EVENTS.EXP_REG_SUCCESS_VIEWED)
        }
    },

    [ANALYTICS_DESKTOP_SUCCESS_DONE_CLICKED]: (getState, action) => {
        const modal = getState().getIn(['modal', 'modalStack', 0])
        if (modal) {
            return checkRegistrationModal(getState, modal.content, EVENTS.EXP_REG_DONEBUTTON_CLICKED)
        }
    },

    [ANALYTICS_EXP_SELECTED]: (getState, action) => {
        const { inst_name, loc_name, owner_id, loc_id } = getTourData()(getState)

        return trackEvent(EVENTS.EXP_EXPSELECTED, {
            Institution: inst_name,
            Location: loc_name,
            Inst_ID: owner_id,
            Loc_ID: loc_id,
        })
    },
}

export default analyticAnalytics
